import { useState } from "react";
import { toast } from "react-toastify";
import { downloadSingleImage, getWatermarkedImageBlob } from "../../utils/groupDetail/downloadHelpers";

export const useGroupDownload = ({
  groupData,
  displayedPhotos,
  selectedPhotos,
  clearSelection,
  previewPhoto,
  currentImageIndex
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  // Download selected photos
  const handleDownloadSelected = async () => {
    if (isDownloading) return;
    if (!selectedPhotos || selectedPhotos.size === 0) {
      toast.error("No photos selected");
      return;
    }

    const photosToDownload = (displayedPhotos || []).filter((p) =>
      selectedPhotos.has(p._id || p.filePath)
    );

    setIsDownloading(true);
    toast.info(
      `⬇️ Downloading ${photosToDownload.length} photo${photosToDownload.length !== 1 ? "s" : ""}...`
    );

    try {
      for (let i = 0; i < photosToDownload.length; i++) {
        await downloadSingleImage(
          photosToDownload[i].filePath,
          photosToDownload[i].fileName || `photo_${i + 1}.jpg`,
          groupData?.watermark
        );
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      toast.success("✅ Download complete!");
      if (clearSelection) clearSelection();
    } finally {
      setIsDownloading(false);
    }
  };

  // Download all photos
  const handleDownloadAll = async () => {
    if (isDownloading) return;
    const photos = displayedPhotos || [];
    if (!photos.length) {
      toast.error("No photos to download");
      return;
    }

    setIsDownloading(true);
    toast.info(
      `⬇️ Downloading ${photos.length} photo${photos.length !== 1 ? "s" : ""}...`
    );

    try {
      for (let i = 0; i < photos.length; i++) {
        await downloadSingleImage(
          photos[i].filePath,
          photos[i].fileName || `photo_${i + 1}.jpg`,
          groupData?.watermark
        );
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      toast.success("✅ Download complete!");
    } finally {
      setIsDownloading(false);
    }
  };

  // Download currently previewed image in lightbox
  const handleDownloadCurrentImage = async () => {
    if (isDownloading || !previewPhoto) return;
    
    setIsDownloading(true);
    let url = null;
    try {
      const blob = await getWatermarkedImageBlob(previewPhoto.filePath, groupData?.watermark);
      url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = previewPhoto.fileName || `photo_${currentImageIndex + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("✅ Watermarked image downloaded!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed");
    } finally {
      if (url) {
        URL.revokeObjectURL(url);
      }
      setIsDownloading(false);
    }
  };

  return {
    isDownloading,
    handleDownloadSelected,
    handleDownloadAll,
    handleDownloadCurrentImage
  };
};
